import { from, Observable, of } from 'rxjs';

/**
 * Name for this browser, sent with "Remember this device" so the authorized-devices list can say
 * what the device really is. The User-Agent header cannot: Chrome on many Android phones and
 * tablets (and any in "Desktop site" mode) sends a desktop agent that reads as Linux.
 * Format: "Chrome on Android" or "Chrome on Android (Pixel 8)".
 */

interface UaHighEntropyValues {
  platform?: string;
  model?: string;
}

interface UaData {
  platform: string;
  getHighEntropyValues(hints: string[]): Promise<UaHighEntropyValues>;
}

/** The parts of `navigator` read here; User-Agent Client Hints are Chromium-only and not in lib.dom. */
export interface DeviceInfoSource {
  userAgent: string;
  maxTouchPoints: number;
  userAgentData?: UaData;
}

const HINT_TIMEOUT_MS = 500;
const MAX_MODEL_LENGTH = 40;
/** The API rejects longer passkey names ([MaxLength(60)] on FriendlyName). */
const MAX_PASSKEY_NAME_LENGTH = 60;

const PLATFORM_NAMES: Record<string, string> = {
  'Chrome OS': 'ChromeOS',
  'Chromium OS': 'ChromeOS',
};

/** Never rejects: a device we cannot name must not hold up sign-in. */
export async function resolveDeviceLabel(
  source: DeviceInfoSource = navigator,
  coarsePointer = matchMedia('(pointer: coarse)').matches
): Promise<string> {
  const browser = browserFromUserAgent(source.userAgent);
  let os = osFromUserAgent(source.userAgent);
  const hints = await readClientHints(source.userAgentData);
  if (hints?.platform && hints.platform !== 'Unknown') {
    os = PLATFORM_NAMES[hints.platform] ?? hints.platform;
  }
  const model = (hints?.model ?? '').trim().slice(0, MAX_MODEL_LENGTH);
  os = correctDesktopModeOs(os, source.maxTouchPoints, coarsePointer);
  return model ? `${browser} on ${os} (${model})` : `${browser} on ${os}`;
}

async function readClientHints(data: UaData | undefined): Promise<UaHighEntropyValues | null> {
  if (!data) {
    return null;
  }
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), HINT_TIMEOUT_MS));
  try {
    return await Promise.race([data.getHighEntropyValues(['platform', 'model']), timeout]);
  } catch {
    return { platform: data.platform };
  }
}

/**
 * A touch-first "desktop" is almost always a phone or tablet asking for the desktop site: iPadOS
 * Safari reports macOS, Android Chrome reports Linux. Touch laptops keep a fine primary pointer.
 */
function correctDesktopModeOs(os: string, maxTouchPoints: number, coarsePointer: boolean): string {
  if (os === 'macOS' && maxTouchPoints > 1) {
    return 'iPadOS';
  }
  if (os === 'Linux' && maxTouchPoints > 0 && coarsePointer) {
    return 'Android';
  }
  return os;
}

/** Order matters: Edge and Opera both carry "Chrome/", and Chrome carries "Safari/". */
function browserFromUserAgent(ua: string): string {
  return /Edg(A|iOS)?\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /(Chrome|CriOS)\//.test(ua) ? 'Chrome'
    : /(Firefox|FxiOS)\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : 'Browser';
}

/** Order matters: an iPhone's agent also says "Mac OS X", and Android's says "Linux". */
function osFromUserAgent(ua: string): string {
  return /iPhone/.test(ua) ? 'iOS'
    : /iPad/.test(ua) ? 'iPadOS'
    : /Android/.test(ua) ? 'Android'
    : /CrOS/.test(ua) ? 'ChromeOS'
    : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : 'Unknown OS';
}

/**
 * Name stored with a new passkey, from the same resolver as trusted devices so an Android phone is not
 * saved as "Linux". A roaming authenticator is not tied to this browser, so it is named for what it is.
 */
export async function resolvePasskeyName(
  attachment: string | null,
  source: DeviceInfoSource = navigator,
  coarsePointer = matchMedia('(pointer: coarse)').matches
): Promise<string> {
  if (attachment === 'cross-platform') {
    return 'Security key or password manager';
  }
  return (await resolveDeviceLabel(source, coarsePointer)).slice(0, MAX_PASSKEY_NAME_LENGTH);
}

/** What the browser told us, used only when the authenticator reported no provider we recognise. */
const PLATFORM_STORAGE = 'This device';
const ROAMING_STORAGE = 'Security key or password manager';

/**
 * Where the "Passkey added" screen says the new passkey went. The API names the provider from the AAGUID the
 * authenticator reported ("Dashlane", "Windows Hello", "iCloud Keychain"), the same name Security >
 * Authentication > Passkey lists it under, so that wins. Without one, all we know is what the browser said:
 * a platform authenticator is on this device, anything else is a key or a manager.
 */
export function passkeyStorageHint(providerName: string | null | undefined, attachment: string | null): string {
  return providerName?.trim() || (attachment === 'platform' ? PLATFORM_STORAGE : ROAMING_STORAGE);
}

/** The label only when the device is being remembered; otherwise nothing extra is sent. */
export function deviceLabelIfTrusted(trustDevice: boolean): Observable<string | undefined> {
  return trustDevice ? from(resolveDeviceLabel()) : of(undefined);
}
